// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\SuppliersCatalog.jsx

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Context } from "../store/appContext";
import getApiBaseUrl from "../utils/api";
import "../styles/layout.css";
import "../styles/utilities.css";
import "../styles/forms.css";
import "../styles/catalog.css";

const currencyFormatter = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));
const formatInteger = (value) => numberFormatter.format(Number(value || 0));
const PAGE_SIZE = 10;
const API_URL = getApiBaseUrl();

const getAverageUnitPrice = (row) =>
  row?.average_unit_price ?? row?.avg_unit_price ?? row?.average_price ?? null;

const getSortIndicator = (sortConfig, key) => {
  if (sortConfig.key !== key) {
    return "";
  }

  return sortConfig.direction === "asc" ? " ▲" : " ▼";
};

const SuppliersCatalog = () => {
  const { store, actions } = useContext(Context);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: PAGE_SIZE,
    total: 0,
    pages: 0,
  });
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const isAdmin = store.role === "admin";
  const token = store.token;

  useEffect(() => {
    const controller = new AbortController();

    const loadSuppliers = async () => {
      if (!token) {
        setRows([]);
        setSummary(null);
        return;
      }

      const params = new URLSearchParams({
        page: String(currentPage),
        per_page: String(PAGE_SIZE),
      });
      const normalizedSearch = appliedSearch.trim();
      if (normalizedSearch) params.set("search", normalizedSearch);
      if (sortConfig.key) {
        params.set("sort_by", sortConfig.key);
        params.set("sort_order", sortConfig.direction);
      }

      setIsLoading(true);
      setErrorMessage("");

      try {
        const response = await fetch(
          `${API_URL}/api/catalog/suppliers?${params.toString()}`,
          {
            headers: { Authorization: "Bearer " + token },
            signal: controller.signal,
          },
        );
        const payload = await response.json().catch(() => null);

        if (response.status === 401) {
          await actions.handleApiError(response);
          return;
        }

        if (!response.ok || !payload?.success) {
          setRows([]);
          setSummary(null);
          setErrorMessage(payload?.error || "No se pudo cargar el catálogo");
          return;
        }

        setRows(Array.isArray(payload.data?.rows) ? payload.data.rows : []);
        setPagination((current) => ({
          ...current,
          ...(payload.data?.pagination || {}),
        }));
        setSummary(payload.data?.summary || null);
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Suppliers catalog load error:", error);
        setRows([]);
        setSummary(null);
        setErrorMessage("No se pudo cargar el catálogo");
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadSuppliers();

    return () => controller.abort();
  }, [appliedSearch, currentPage, sortConfig, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setAppliedSearch(search.trim());
    setCurrentPage(1);
  };

  const handleClear = async () => {
    setSearch("");
    setAppliedSearch("");
    setCurrentPage(1);
  };

  const handleOpenDetail = (supplierId) => {
    if (!supplierId) {
      return;
    }

    navigate(`/suppliers/${encodeURIComponent(supplierId)}`);
  };

  const handleExport = async () => {
    await actions.exportSuppliersCatalogExcel({
      search,
      sort_by: sortConfig.key,
      sort_dir: sortConfig.direction,
    });
  };

  const totalRows = Number(pagination.total || 0);
  const totalPages = Math.max(1, Number(pagination.pages || 1));
  const firstRowIndex = (currentPage - 1) * PAGE_SIZE;
  const lastRowIndex = Math.min(firstRowIndex + rows.length, totalRows);
  const hasAverageUnitPrice = rows.some(
    (row) => getAverageUnitPrice(row) !== null && getAverageUnitPrice(row) !== undefined,
  );
  const paginatedRows = rows;
  const topSupplier = summary?.top_supplier || rows[0] || null;

  const kpis = useMemo(
    () =>
      rows.reduce(
        (accumulator, row) => ({
          totalSuppliers: accumulator.totalSuppliers + 1,
          totalSpend: accumulator.totalSpend + Number(row.total_spend || 0),
          purchaseLines:
            accumulator.purchaseLines + Number(row.purchase_lines || 0),
          distinctParts:
            accumulator.distinctParts + Number(row.distinct_parts || 0),
        }),
        {
          totalSuppliers: 0,
          totalSpend: 0,
          purchaseLines: 0,
          distinctParts: 0,
        }
      ),
    [rows]
  );
  const displayKpis = summary
    ? {
        totalSuppliers: summary.total_suppliers,
        totalSpend: summary.total_spend,
        purchaseLines: summary.purchase_lines,
        distinctParts: summary.distinct_parts,
      }
    : kpis;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSort = async (key) => {
    const nextSortConfig = {
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc",
    };
    setSortConfig(nextSortConfig);
    setCurrentPage(1);
  };

  const renderSortableHeader = (key, label, className = "") => (
    <th className={className}>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none text-reset fw-semibold"
        onClick={() => handleSort(key)}
        disabled={isLoading}
      >
        {label}
        {getSortIndicator(sortConfig, key)}
      </button>
    </th>
  );

  return (
    <div className="container mt-5 catalog-page">
      <div className="page-header catalog-header">
        <div className="catalog-title-block">
          <span className="catalog-title-icon" aria-hidden="true">
            🏢
          </span>
          <div>
            <h1 className="page-title">Catálogo de proveedores</h1>
            <p className="page-subtitle">
              Ranking de proveedores según compras registradas en almacén.
            </p>
          </div>
        </div>
      </div>

      <section className="catalog-kpi-grid" aria-label="Indicadores de proveedores">
        <article className="catalog-kpi-card catalog-kpi-card-suppliers">
          <span className="catalog-kpi-icon" aria-hidden="true">
            👥
          </span>
          <div>
            <span className="catalog-kpi-label">Total proveedores</span>
            <strong>{formatInteger(displayKpis.totalSuppliers)}</strong>
          </div>
        </article>
        <article className="catalog-kpi-card catalog-kpi-card-spend">
          <span className="catalog-kpi-icon" aria-hidden="true">
            💶
          </span>
          <div>
            <span className="catalog-kpi-label">Gasto total</span>
            <strong>{formatCurrency(displayKpis.totalSpend)}</strong>
          </div>
        </article>
        <article className="catalog-kpi-card catalog-kpi-card-lines">
          <span className="catalog-kpi-icon" aria-hidden="true">
            📄
          </span>
          <div>
            <span className="catalog-kpi-label">Líneas de compra</span>
            <strong>{formatInteger(displayKpis.purchaseLines)}</strong>
          </div>
        </article>
        <article className="catalog-kpi-card catalog-kpi-card-parts">
          <span className="catalog-kpi-icon" aria-hidden="true">
            ⚙️
          </span>
          <div>
            <span className="catalog-kpi-label">Repuestos acumulados</span>
            <strong>{formatInteger(displayKpis.distinctParts)}</strong>
          </div>
        </article>
        <article className="catalog-kpi-card catalog-kpi-card-leader">
          <span className="catalog-kpi-icon" aria-hidden="true">
            🏆
          </span>
          <div>
            <span className="catalog-kpi-label">Proveedor principal</span>
            <strong>{topSupplier?.supplier || "-"}</strong>
          </div>
        </article>
      </section>

      {topSupplier && (
        <section className="catalog-top-supplier-card">
          <div className="catalog-top-supplier-main">
            <span className="catalog-top-supplier-icon" aria-hidden="true">
              🥇
            </span>
            <div>
              <span className="catalog-eyebrow">Top proveedor</span>
              <h2>{topSupplier.supplier || "Proveedor sin nombre"}</h2>
              <p>
                Lidera el ranking por gasto total registrado en el catálogo.
              </p>
            </div>
          </div>
          <div className="catalog-top-supplier-metrics">
            <span>
              <strong>{formatCurrency(topSupplier.total_spend)}</strong>
              Gasto total
            </span>
            <span>
              <strong>{formatInteger(topSupplier.purchase_lines)}</strong>
              Líneas
            </span>
            <button
              type="button"
              className="catalog-detail-button catalog-detail-button-highlight"
              onClick={() => handleOpenDetail(topSupplier.supplier_id)}
              disabled={!topSupplier.supplier_id}
            >
              Ver detalle
            </button>
          </div>
        </section>
      )}

      <form className="catalog-filters" onSubmit={handleSubmit}>
        <div className="catalog-filter-group">
          <label htmlFor="suppliers_catalog_search">Buscar proveedor</label>
          <div className="catalog-search-input-wrap">
            <span aria-hidden="true">🔎</span>
            <input
              id="suppliers_catalog_search"
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Nombre del proveedor"
            />
          </div>
        </div>

        <div className="catalog-filter-actions">
          <button className="btn btn-primary" type="submit" disabled={isLoading}>
            Buscar
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={handleClear}
            disabled={isLoading}
          >
            Limpiar
          </button>
          {isAdmin && (
            <button
              className="btn btn-secondary"
              type="button"
              onClick={handleExport}
              disabled={
                isLoading ||
                store.suppliersCatalogExportLoading
              }
            >
              {store.suppliersCatalogExportLoading
                ? "Exportando..."
                : "Exportar Excel"}
            </button>
          )}
        </div>
      </form>

      {isAdmin && store.suppliersCatalogExportError && (
        <div className="catalog-state catalog-state-error catalog-export-error">
          {store.suppliersCatalogExportError}
        </div>
      )}

      <section className="catalog-card">
        <div className="catalog-section-head">
          <div>
            <h2>Ranking por gasto</h2>
            <p>
              Proveedores ordenados por volumen de compra acumulado.
            </p>
          </div>
          <span className="catalog-table-count">
            {totalRows > 0
              ? `${formatInteger(firstRowIndex + 1)}-${formatInteger(lastRowIndex)} de ${formatInteger(totalRows)}`
              : `0 de ${formatInteger(totalRows)}`}
          </span>
        </div>

        {isLoading && (
          <div className="catalog-state catalog-state-loading catalog-state-inline">
            Cargando catálogo de proveedores...
          </div>
        )}

        {!isLoading && errorMessage && (
          <div className="catalog-state catalog-state-error catalog-state-inline">
            {errorMessage}
          </div>
        )}

        {!isLoading && !errorMessage && rows.length === 0 && (
          <div className="catalog-state catalog-state-empty catalog-state-inline">
            No hay proveedores que coincidan con la búsqueda actual.
          </div>
        )}

        {!errorMessage && rows.length > 0 && (
          <div className="table-responsive catalog-table-scroll suppliers-catalog-table-wrap">
              <table className="table table-compact mb-0 suppliers-catalog-table">
                <thead>
                  <tr>
                    <th>Ranking</th>
                    <th>ID proveedor</th>
                    {renderSortableHeader("supplier", "Proveedor")}
                    {renderSortableHeader("total_spend", "Gasto total", "text-end")}
                    {renderSortableHeader("purchase_lines", "Líneas de compra", "text-end")}
                    {renderSortableHeader("distinct_parts", "Repuestos distintos", "text-end")}
                    {hasAverageUnitPrice &&
                      renderSortableHeader("average_unit_price", "Precio medio", "text-end")}
                    <th className="text-end">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((row, index) => {
                    const rank = firstRowIndex + index + 1;

                    return (
                      <tr key={row.supplier_id || row.supplier}>
                        <td>
                          <span className={`catalog-rank catalog-rank-${rank}`}>
                            #{rank}
                          </span>
                        </td>
                        <td className="cell-strong">{row.supplier_id || "-"}</td>
                        <td>
                          {row.supplier_id ? (
                            <button
                              type="button"
                              className="catalog-supplier-name-button"
                              onClick={() => handleOpenDetail(row.supplier_id)}
                            >
                              {row.supplier || "-"}
                            </button>
                          ) : (
                            row.supplier || "-"
                          )}
                        </td>
                        <td className="text-end">
                          <span className="catalog-chip catalog-chip-spend">
                            {formatCurrency(row.total_spend)}
                          </span>
                        </td>
                        <td className="text-end">
                          <span className="catalog-chip catalog-chip-lines">
                            {formatInteger(row.purchase_lines)}
                          </span>
                        </td>
                        <td className="text-end">
                          <span className="catalog-chip catalog-chip-parts">
                            {formatInteger(row.distinct_parts)}
                          </span>
                        </td>
                        {hasAverageUnitPrice && (
                          <td className="text-end">
                            {formatCurrency(getAverageUnitPrice(row))}
                          </td>
                        )}
                        <td className="text-end">
                          <button
                            type="button"
                            className="catalog-detail-button"
                            onClick={() => handleOpenDetail(row.supplier_id)}
                            disabled={!row.supplier_id}
                          >
                            Ver detalle
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
          </div>
        )}

        <div className="catalog-pagination">
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            disabled={isLoading || currentPage === 1}
          >
            Anterior
          </button>
          <span className="catalog-pagination-info">
            Página <strong>{formatInteger(currentPage)}</strong> de{" "}
            <strong>{formatInteger(totalPages)}</strong>
          </span>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() =>
              setCurrentPage((page) => Math.min(totalPages, page + 1))
            }
            disabled={isLoading || currentPage === totalPages}
          >
            Siguiente
          </button>
        </div>
      </section>
    </div>
  );
};

export default SuppliersCatalog;
